import { Component, Input, OnInit, ViewChildren, QueryList } from '@angular/core';
import { take } from 'rxjs/operators';

import { ApiError, MessageService, notCloseMessageOptions } from '../../../../core';
import { ClienteUnidadeNegocioHttpService } from './services';
import { UnidadeNegocio, unidadeNegocioAtiva, UnidadeNegocioFilter, UnidadeNegocioCrudHttpService } from '../../../unidade-negocio';
import { ClienteUnidadeNegocioSearch } from './cliente-unidade-negocio-search';
import { ClienteUnidadeNegocioMercadoComponent } from './mercado';
import { ItemTipo, itemTipoAtivo, ItemTipoFilter, ItemTipoCrudHttpService } from '../../../item-tipo';
import { ClienteUnidadeNegocio } from './cliente-unidade-negocio';

@Component({
    selector: 'sgt-cliente-unidade-negocio2',
    templateUrl: './cliente-unidade-negocio2.component.html',
    styleUrls: ['./cliente-unidade-negocio2.component.less']
})
export class ClienteUnidadeNegocio2Component implements OnInit {
    isLoading: boolean;
    isLoaded: boolean;
    entities: ClienteUnidadeNegocioSearch[] = [];
    unidadesNegocio: UnidadeNegocio[] = [];
    itensTipos: ItemTipo[] = [];

    @Input() canEdit = true;
    /** ID do cliente */
    @Input() idCliente: number;
    /** Tipos de itens cadastrados no cliente */
    // @Input() itensTipos: ItemTipo[] = [];


    /** Forms dos mercados */
    @ViewChildren('mercadoForm') mercadosForms: QueryList<ClienteUnidadeNegocioMercadoComponent>;

    constructor(private messageService: MessageService,
                private unidadeNegocioCrudHttpService: UnidadeNegocioCrudHttpService,
                private clienteUnidadeNegocioHttpService: ClienteUnidadeNegocioHttpService,
                private itemTipoCrudHttpService: ItemTipoCrudHttpService) { }

    ngOnInit() {
        this.searchUnidadesNegocio();
        this.searchItensTipos();
    }

    /** Pesquisas as unidades de negócio cadastradas */
    private searchUnidadesNegocio() {
        const filter = new UnidadeNegocioFilter();
        filter.situacao = unidadeNegocioAtiva.id;
        this.unidadeNegocioCrudHttpService.searchWithFilter(filter)
            .subscribe(
                entities => this.unidadesNegocio = entities,
                (error: ApiError) => this.messageService.error(error.message, 'Erro ao pesquisar as unidades de negócios', error)
            );
    }

    /** Pesquisas os tipos de itens */
    private searchItensTipos() {
        this.itensTipos = [];
        const filter = new ItemTipoFilter();
        filter.situacao = itemTipoAtivo.id;
        this.itemTipoCrudHttpService.searchWithFilter(filter)
            .subscribe(
                entities => this.itensTipos = entities,
                (error: ApiError) => this.messageService.error(error.message, 'Erro ao pesquisar os tipos de produtos', error)
            );
    }

    /** Pesquisa as unidades de negócios do cliente */
    search() {
        this.entities = [];
        this.isLoading = true;
        this.isLoaded = false;
        this.clienteUnidadeNegocioHttpService.search(this.idCliente)
            .pipe(
                take(1)
            )
            .subscribe(
                entities => {
                    this.entities = entities;
                    this.isLoading = false;
                    this.isLoaded = true;
                },
                (error: ApiError) => {
                    this.messageService.error(error.message, 'Erro ao pesquisar as unidades de negócios do cliente');
                    this.isLoading = false;
                }
            );
    }

    insert() {
        if (!this.canEdit) {
            return;
        }

        const entity = new ClienteUnidadeNegocioSearch();
        this.entities.push(entity);
    }

    delete(index: number) {
        if (index < 0 || !this.canEdit) {
            return;
        }

        const entity = this.entities[index];
        this.messageService.confirm(`Deseja realmente remover a unidade de negócio ${entity?.nomeUnidadeNegocio ?? ''} do cliente?`, 'Confirme').then(result => {
            if (result) {
                this.entities.splice(index, 1);
            }
        });
    }

    /** Ao selecionar a unidade de negócio */
    onSelectUnidadeNegocio(entity: ClienteUnidadeNegocioSearch, idUnidadeNegocio: number) {
        if (idUnidadeNegocio) {
            entity.idUnidadeNegocio = idUnidadeNegocio;

            const unidadeNegocio = this.unidadesNegocio?.find(unidadeNegocioFilter => unidadeNegocioFilter.id === idUnidadeNegocio);
            entity.nomeUnidadeNegocio = unidadeNegocio?.nome ?? null;
        } else {
            entity.idUnidadeNegocio = null;
            entity.nomeUnidadeNegocio = null;
        }
    }

    /** Verifica se as unidades de negócios estão válidas */
    isValid(): boolean {
        if (!this.canEdit) {
            return true;
        }

        // Verificando se os mercados estão válidos
        let isClientesMercadosValid = true
        this.mercadosForms?.forEach(mercadoForm => {
            if (!mercadoForm.isValid()) {
                isClientesMercadosValid = false;
            }
        });
        if (!isClientesMercadosValid) {
            return false;
        }

        // Verificando se as unidades de negócios estão válidas
        for (let i = 0; i < this.entities.length; i++) {
            const entity = this.entities[i];
            if (!entity.idUnidadeNegocio) {
                this.messageService.warning('Informe a unidade de negócio ou remova o registro', 'A unidade de negócio é obrigatório', notCloseMessageOptions);
                return false;
            }

            const count = this.entities.filter(entityFilter => entityFilter.idUnidadeNegocio && entityFilter.idUnidadeNegocio === entity.idUnidadeNegocio).length;
            if (count > 1) {
                this.messageService.warning(`A unidade de negócio ${entity.nomeUnidadeNegocio ?? ''} já está adicionada no cliente. Remova ou altere a unidade de negócio duplicada`, 'Não é possível adicionar duas unidades de negócio iguais no mesmo cliente', notCloseMessageOptions);
                return false;
            }
        }
        return true;
    }

    /** Resolve as unidades de negócios */
    resolve(): ClienteUnidadeNegocio[] {
        if (!this.entities) {
            return;
        }

        // Criando os objetos para salvar as unidades de negócios com seus mercados, naturezas de vendas e tipos de itens
        const clientesUnidadesNegocios: ClienteUnidadeNegocio[] = [];
        // Percorrendo as unidades de negócios
        this.entities.forEach(entityUnidadeNegocio => {
            // Se possuir algum mercado, então percorre os mercados, suas naturezas de vendas e tipos de itens e cria os objetos através deles
            if (entityUnidadeNegocio.mercados?.length > 0) {
                // Percorrendo os mercados
                entityUnidadeNegocio.mercados.forEach(entityMercado => {
                    // Se possuir alguma natureza de venda, então percorre as naturezas de vendas e cria os objetos através delas
                    if (entityMercado.naturezasVendas?.length > 0) {

                        // Percorrendo as naturezas de vendas
                        entityMercado.naturezasVendas.forEach(entityNaturezaVenda => {

                            // Se possuir algum tipo de item, então percorre os tipos de itens e cria os objetos através deles
                            if (entityNaturezaVenda.itensTipos?.length > 0) {
                                entityNaturezaVenda.itensTipos.forEach(entityItemTipo => {
                                    const clienteUnidadeNegocio = new ClienteUnidadeNegocio();
                                    clienteUnidadeNegocio.id = entityItemTipo.id;
                                    clienteUnidadeNegocio.createdAt = entityItemTipo.createdAt;
                                    clienteUnidadeNegocio.idCliente = this.idCliente;
                                    clienteUnidadeNegocio.idUnidadeNegocio = entityUnidadeNegocio.idUnidadeNegocio;
                                    clienteUnidadeNegocio.idMercado = entityMercado.idMercado;
                                    clienteUnidadeNegocio.idNaturezaVenda = entityNaturezaVenda.idNaturezaVenda;
                                    clienteUnidadeNegocio.idItemTipo = entityItemTipo.idItemTipo;
                                    clientesUnidadesNegocios.push(clienteUnidadeNegocio);
                                });
                            } else {
                                // Se não, cria o objeto através da natureza de venda
                                const clienteUnidadeNegocio = new ClienteUnidadeNegocio();
                                clienteUnidadeNegocio.id = entityNaturezaVenda.id;
                                clienteUnidadeNegocio.createdAt = entityNaturezaVenda.createdAt;
                                clienteUnidadeNegocio.idCliente = this.idCliente;
                                clienteUnidadeNegocio.idUnidadeNegocio = entityUnidadeNegocio.idUnidadeNegocio;
                                clienteUnidadeNegocio.idMercado = entityMercado.idMercado;
                                clienteUnidadeNegocio.idNaturezaVenda = entityNaturezaVenda.idNaturezaVenda;
                                clientesUnidadesNegocios.push(clienteUnidadeNegocio);
                            }
                        });
                    } else {
                        // Se não, cria o objeto através do mercado
                        const clienteUnidadeNegocio = new ClienteUnidadeNegocio();
                        clienteUnidadeNegocio.id = entityMercado.id;
                        clienteUnidadeNegocio.createdAt = entityMercado.createdAt;
                        clienteUnidadeNegocio.idCliente = this.idCliente;
                        clienteUnidadeNegocio.idUnidadeNegocio = entityUnidadeNegocio.idUnidadeNegocio;
                        clienteUnidadeNegocio.idMercado = entityMercado.idMercado;
                        clientesUnidadesNegocios.push(clienteUnidadeNegocio);
                    }
                });
            } else {
                // Se não, cria o objeto através da unidade de negócio
                const clienteUnidadeNegocio = new ClienteUnidadeNegocio();
                clienteUnidadeNegocio.id = entityUnidadeNegocio.id;
                clienteUnidadeNegocio.createdAt = entityUnidadeNegocio.createdAt;
                clienteUnidadeNegocio.idCliente = this.idCliente;
                clienteUnidadeNegocio.idUnidadeNegocio = entityUnidadeNegocio.idUnidadeNegocio;
                clientesUnidadesNegocios.push(clienteUnidadeNegocio);
            }
        });

        return clientesUnidadesNegocios;
    }
}
